import {FreeDeliverySection} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/basket_mov.types';
import {GetProductBulkResponse} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/article_detail_page/v1/service.types';
import {AddItemResponseStatus_Success_Section} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/basket/v1/add_item_status.types';
import {
  useTrackFeature,
  useTrackFeatureInViewImpression,
} from '@aboutyou/tracking';
import {Flyout, SizeBubbleList} from '@aboutyou/ui-components';
import {BasketMutationSuccess} from '@aboutyou/user';
import React, {useEffect, useMemo, useState} from 'react';
import styled from 'styled-components';
import {AddToBasketSuccessFlyoutWithRecos} from '../../../features/AddToBasketSuccessFlyout/AddToBasketSuccessFlyoutWithRecos';
import {useSuccessFlyoutOpenedState} from '../../../features/AddToBasketSuccessFlyout/hooks/useSuccessFlyoutOpenedState';
import {
  SizeSectionSizeReminder,
  useSizeReminderState,
} from '../../../features/SizeSelection/SizeSectionSizeReminder';
import {usePreselectSize} from '../../../features/SizeSelection/hooks/usePreselectSize';
import {useRecommendedSize} from '../../../features/SizeSelection/hooks/useRecommendedSize';
import {
  SelectedSize,
  SetSelectedSize,
} from '../../../features/SizeSelection/hooks/useSelectedSizeState';
import {AddToBasketFlyout} from '../../../features/SizeSelectionFlyout/AddToBasketFlyout';
import {AddToBasketOnSuccessProps} from '../../../features/SizeSelectionFlyout/hooks/useAddToBasket';
import {
  SizeSelectionOpenedStateKey,
  useSizeSelectionOpenedState,
} from '../../../features/SizeSelectionFlyout/hooks/useSizeSelectionOpenedState';
import {useTilesSlider} from '../../BrandHub/utils/useTilesSlider';
import {AddToBasket} from '../AddToBasketSection/AddToBasket/AddToBasket';
import {getSizeFinderRecommendations} from '../AddToBasketSection/getSizeFinderRecommendations';
import {useIsQuickView} from '../QuickView/useIsQuickView';
import {useGetBadgeListKeys} from '../utils/useGetBadgeListKeys';
import {useInlineSizeSelectionQuickViewAB} from './hooks/useInlineSizeSelectionQuickViewAB';

export interface AddToBasketQuickViewViewProps {
  selectedSize: SelectedSize;
  setSelectedSize: SetSelectedSize;
  className?: string;
  onAddToBasketSuccess?: (props: AddToBasketOnSuccessProps) => void;
  disableBasketRecommendations?: boolean;
  filteredSizeId?: number;
  sizeSelectionStateManagerKey: SizeSelectionOpenedStateKey;
  freeDeliverySection?: FreeDeliverySection;
  statusSuccessSections?: AddItemResponseStatus_Success_Section[];
}

export const AddToBasketQuickViewView: React.FC<
  GetProductBulkResponse['sizesSection'] & AddToBasketQuickViewViewProps
> = ({
  brand,
  product,
  sizeSelection,
  selectedSize,
  setSelectedSize,
  saleBadges,
  onAddToBasketSuccess,
  filteredSizeId,
  sizeSelectionStateManagerKey,
  className,
  freeDeliverySection,
  statusSuccessSections = [],
}) => {
  const {isOpen: isSizeSelectionOpen, cancel: cancelSizeSelection} =
    useSizeSelectionOpenedState(sizeSelectionStateManagerKey);
  const isQuickView = useIsQuickView();
  const recommendedSize = useRecommendedSize({sizeSelection});
  const [showSoldOutSizeReminder, setShowSoldOutSizeReminder] = useState(false);
  const isInlineSizeSelectionQuickViewAB = useInlineSizeSelectionQuickViewAB();
  const {ref: trackingRef} = useTrackFeatureInViewImpression<HTMLDivElement>({
    label: 'size_selection_tiles',
  });
  const {badgesListKeys} = useGetBadgeListKeys({
    isInMiniADPDialog: false, // not considered mini ADP, since this is Quick View
  });
  const {scrollWrapperRef, scrollToSlide} = useTilesSlider();
  const trackFeature = useTrackFeature();

  const {isOpen: isSuccessFlyoutOpen, cancel: closeSuccessFlyout} =
    useSuccessFlyoutOpenedState();

  const {sizeReminderSize, showSizeReminder} = useSizeReminderState(
    sizeSelection.type?.$case === 'oneSize' && sizeSelection.type?.oneSize.size
      ? [sizeSelection.type?.oneSize.size.sizeId]
      : [],
  );

  useEffect(() => {
    // we are only updating selectedSize which is set on page level
    // then we wait for it to return and set showSizereminder that is used by size reminder
    // we are doing this to avoid another selected size state local to this component
    showSizeReminder(selectedSize);
  }, [selectedSize]);

  const sizes = useMemo(() => {
    switch (sizeSelection.type?.$case) {
      case 'sizes':
        return sizeSelection.type.sizes.sizes;
      case 'groupedSizes': {
        if (
          sizeSelection.type.groupedSizes.groups[0]?.content?.$case ===
          'sizeRuns'
        ) {
          return sizeSelection.type.groupedSizes.groups[0]?.content.sizeRuns
            .sizeRuns[0]?.sizes;
        } else {
          return sizeSelection.type.groupedSizes.groups[0]?.content?.sizes
            .sizes;
        }
      }
      case 'sizeRuns':
        return sizeSelection.type.sizeRuns.sizeRuns[0]?.sizes;
      case 'inlineSelection':
        return sizeSelection.type.inlineSelection.sizes;
    }
  }, [sizeSelection]);

  usePreselectSize({
    productId: product.id,
    setSelectedSize,
    sizeSelection,
    filteredSizeId,
    recommendedSize,
    isSizeSelectionOpen:
      isInlineSizeSelectionQuickViewAB || isSizeSelectionOpen,
    selectedSizeId: selectedSize?.sizeId,
  });

  useEffect(() => {
    const selectedItemIndex = sizes?.findIndex(
      item => item.sizeId === selectedSize?.sizeId,
    );
    if (selectedItemIndex && selectedItemIndex > -1) {
      scrollToSlide(selectedItemIndex, {
        behavior: 'smooth',
      });
    }
  }, [scrollToSlide]);

  const sizeFinderRecommendationsCallback = useMemo(
    () => getSizeFinderRecommendations(sizeSelection),
    [sizeSelection],
  );

  if (!sizeSelection || product.isSoldOut) {
    return null;
  }

  const handleOpenerClose = () => {
    cancelSizeSelection();
  };

  const onSizeSelect = (
    selectedSizeId: number,
    isOutOfStock?: boolean | undefined,
  ) => {
    if (isInlineSizeSelectionQuickViewAB) {
      trackFeature({eventType: 'click', label: 'size_selection_tiles'});
    }
    const foundSize = sizes?.find(item => item.sizeId === selectedSizeId);
    if (isOutOfStock) {
      setShowSoldOutSizeReminder(true);
      showSizeReminder(foundSize);
      return;
    }
    setSelectedSize(foundSize);
  };

  const handleAddToBasketSuccess = ({
    statusSuccessSections,
    freeDeliverySection,
  }: Omit<BasketMutationSuccess, 'failed'>) => {
    onAddToBasketSuccess?.({freeDeliverySection, statusSuccessSections});
  };

  return (
    <Section
      className={className}
      $largerGap={isInlineSizeSelectionQuickViewAB}
    >
      {sizeSelection.type?.$case !== 'oneSize' && (
        <>
          {isInlineSizeSelectionQuickViewAB && (
            <div ref={trackingRef}>
              <SizeBubbleList
                ref={scrollWrapperRef}
                sizes={sizes!}
                selectedSizeIds={
                  selectedSize?.sizeId ? [selectedSize?.sizeId] : []
                }
                onSizeSelect={onSizeSelect}
                showOutOfStock={true}
                showRecommendedSize={true}
                recommendedSizeId={recommendedSize?.sizeId}
                isInlineSizeQuickViewEnabled={isInlineSizeSelectionQuickViewAB}
              />
            </div>
          )}
          <SizeSectionSizeReminder
            brand={brand}
            product={product!}
            SizeReminderLayoutComponent={Flyout}
            sizeReminderSize={sizeReminderSize}
            onResetSizeReminderSize={() => {
              setShowSoldOutSizeReminder(false);
            }}
            sizeSelectionOpenedStateKey={undefined}
            isOpen={showSoldOutSizeReminder}
          />

          {!isInlineSizeSelectionQuickViewAB && (
            <AddToBasketFlyout
              isOpen={isSizeSelectionOpen}
              withSuccessToast={false}
              withErrorToast
              sizeSelectionData={{
                sizeSelection,
                product,
                brand,
                saleBadges,
              }}
              product={product}
              onClose={handleOpenerClose}
              preselectedSize={selectedSize}
              onSuccess={({
                size,
                freeDeliverySection,
                statusSuccessSections,
              }) => {
                handleAddToBasketSuccess?.({
                  freeDeliverySection,
                  statusSuccessSections,
                });

                setSelectedSize(size);
              }}
              disableCouponsSlider
              badgesListKeys={badgesListKeys}
            />
          )}
        </>
      )}

      <AddToBasket
        sizeFinderRecommendationsCallback={sizeFinderRecommendationsCallback}
        size={
          sizeSelection.type?.$case === 'oneSize'
            ? sizeSelection.type.oneSize.size
            : selectedSize
        }
        product={product}
        brandName={brand.name}
        onAddToBasketSuccess={handleAddToBasketSuccess}
        recommendedSize={recommendedSize}
        // According to BE inlineSelection is the size type use for Beauty products
        // So far the only way to know if a product is a beauty product :)
        inlineSizeSelection={sizeSelection.type?.$case === 'inlineSelection'}
        sizeSelectionOpenedStateKey={sizeSelectionStateManagerKey}
        isQuickView={isQuickView}
        isOneSize={sizeSelection.type?.$case === 'oneSize'}
        isNewMobileQuickViewActive
        badgesList={badgesListKeys}
      />
      <AddToBasketSuccessFlyoutWithRecos
        productId={product?.id ?? product.id}
        show={isSuccessFlyoutOpen}
        sizeSelectionKey={sizeSelectionStateManagerKey}
        onClose={() => {
          closeSuccessFlyout();
        }}
        freeDeliverySection={freeDeliverySection}
        statusSuccessSections={statusSuccessSections}
        selectedSize={selectedSize}
      />
    </Section>
  );
};

const Section = styled.section<{$largerGap?: boolean}>`
  display: flex;
  flex-direction: column;
  gap: ${props => (props.$largerGap ? '16px' : '12px')};

  &&& {
    padding: 0;
    margin: 0;
  }
`;
